import axios from "axios"
import * as React from "react"
import * as BS from "react-bootstrap"
import styles from "./styles.module.css"
import { FormikErrors } from "formik"
import { EditingAssessmentQuestionProps } from "../Assessment/EditingAssessmentQuestion"
import useBoolean from "helpers/useBoolean"
const { list, itemText, item, selectedItem, unselectedItem, deleteText, tooltip, tooltiptext, star } = styles

type Standard = {
    id: string
    standard: string
    index: number
}

type Topic = {
    id: string
    topic: string
    index: number
}

type Subtopic = {
    id: string
    subtopic: string
    index: number
    description: string
}

type SelectedStandard = {
    standard: Standard
    topic: Topic
    subtopic: Subtopic
    core: boolean
}

type Props = {
    assessmentQuestionId: string
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<EditingAssessmentQuestionProps>>
}

/** This component is the same as the one found in /backend/src/frontend/components  */

export default function AssessmentQuestionStandards({ assessmentQuestionId, setFieldValue }: Props) {

    const [standards, setStandards] = React.useState<Standard[]>([])
    const [topics, setTopics] = React.useState<Topic[]>([])
    const [subtopics, setSubtopics] = React.useState<Subtopic[]>([])

    const [selectedStandard, setSelectedStandard] = React.useState<number>(null)
    const [selectedTopic, setSelectedTopic] = React.useState<number>(null)
    const [selectedSubtopic, setSelectedSubtopic] = React.useState<number>(null)
    const [core, setCore] = React.useState<boolean>(false)

    const [selectedStandards, setSelectedStandards] = React.useState<SelectedStandard[]>([])

    const [expandStandards, toggleStandards] = useBoolean(false)

    React.useEffect(() => {
        void axios.get(`/api/assessments/questions/${assessmentQuestionId}/standards`).then(({ data }) => {
            setSelectedStandards(data ?? [])
        })

        void axios.get("/api/standards").then(({ data }) => {
            setStandards(data.standards ?? [])
        })
    }, [assessmentQuestionId])

    React.useEffect(() => {
        setSelectedTopic(null)
        setSelectedSubtopic(null)
        if (selectedStandard === null) {
            setTopics([])
        } else {
            void axios.get(`/api/standards/${standards[selectedStandard].id}/topics`).then(({ data }) => {
                setTopics(data.topics ?? [])
            })
        }
    }, [selectedStandard, standards])

    React.useEffect(() => {
        if (selectedStandard === null) return
        setSelectedSubtopic(null)
        if (selectedTopic === null) {
            setSubtopics([])
        } else {
            void axios.get(`/api/standards/${standards[selectedStandard].id}/topics/${topics[selectedTopic].id}/subtopics`).then(({ data }) => {
                setSubtopics(data.subtopics ?? [])
            })
        }
    }, [selectedTopic, selectedStandard, standards, topics])

    const addStandard = () => {
        const alreadySelected = selectedStandards.some((selected => selected.subtopic.id === subtopics[selectedSubtopic].id))
        if (alreadySelected) {
            setSelectedStandard(null)
            setSelectedTopic(null)
            setSelectedSubtopic(null)
            return alert("This standard has already been selected.")
        }

        setSelectedStandards([...selectedStandards, {
            standard: standards[selectedStandard],
            topic: topics[selectedTopic],
            subtopic: subtopics[selectedSubtopic],
            core: core,
        }])
        setSelectedStandard(null)
        setSelectedTopic(null)
        setSelectedSubtopic(null)
        setCore(false)
    }

    const deleteStandard = (standardId: string) => {
        setSelectedStandards(selectedStandards.filter(({ standard }) => standard.id !== standardId))
    }

    React.useEffect(() => {
        setFieldValue("subtopicIds", selectedStandards.map(s => ({subtopicId: s.subtopic.id, core: s.core})))
    }, [selectedStandards, setFieldValue])

    return <>
        <input type="hidden" name="subtopicIds" value={JSON.stringify(selectedStandards.map(s => ({subtopicId: s.subtopic.id, core: s.core})))} />
        {selectedStandards.length > 0 &&
            <BS.Row style={{ cursor: "default" }}>
                <BS.Col sm="12">
                    <ul className={list}>
                        <p>Selected Standards</p>
                        {selectedStandards.map((s, i) =>
                            <li key={i} className={tooltip}>
                                <div className={`${item} ${selectedItem}`}>
                                    <div className={itemText}>
                                        {s.core && <i className={`fas fa-star ${star}`} />}
                                        <span> {s.standard.standard}</span>
                                    </div>
                                    <div className={itemText}>{s.topic.topic}</div>
                                    <div className={itemText}>{s.subtopic.subtopic}</div>
                                    <div className={`${itemText} ${deleteText}`} onClick={() => deleteStandard(s.standard.id)}>delete</div>
                                </div>
                                {s.subtopic.description && <span className={tooltiptext}>{s.subtopic.description}</span>}
                            </li>
                        )}
                    </ul>
                </BS.Col>
            </BS.Row>}
        <BS.Row style={{ cursor: "default" }}>
            <BS.Col sm="12">
                <ul className={list}>
                    {selectedStandard !== null && <>
                        <p>Currently Selected</p>
                        <li onClick={() => setSelectedStandard(null)}>
                            <div className={`${item} ${selectedItem}`}>
                                <div className={itemText}>{standards[selectedStandard].standard}</div>
                            </div>
                        </li>
                    </>}

                    {selectedTopic !== null &&
                        <li onClick={() => setSelectedTopic(null)}>
                            <div className={`${item} ${selectedItem}`}>
                                <div className={itemText}>{topics[selectedTopic].topic}</div>
                            </div>
                        </li>
                    }

                    {selectedSubtopic !== null &&
                        <li onClick={() => setSelectedSubtopic(null)} className={tooltip}>
                            <div className={`${item} ${selectedItem}`}>
                                <div className={itemText}>{subtopics[selectedSubtopic].subtopic}</div>
                            </div>
                            {subtopics[selectedSubtopic].description && <span className={tooltiptext}>{subtopics[selectedSubtopic].description}</span>}
                        </li>
                    }
                </ul>
            </BS.Col>
        </BS.Row>
        <BS.Row style={{ cursor: "default" }}>
            <BS.Col sm="12">
                {selectedStandard === null && 
                    <div>
                        <p role="button" onClick={toggleStandards}>
                            Available Standards <i className={`fas ${expandStandards ? `fa-chevron-up` : `fa-chevron-down`} ms-2`} />
                        </p>
                         {expandStandards &&
                            <ul className={list}>
                                {standards.map((standard, i) => (
                                    <li key={i} className={`${item} ${unselectedItem}`} onClick={() => setSelectedStandard(i)}>{standard.standard}</li>
                                ))}
                            </ul>
                        }
                    </div>}
                {selectedStandard !== null && selectedTopic === null &&
                    <ul className={list}>
                        {topics.map((topic, i) => (
                            <li key={i} className={`${item} ${unselectedItem}`} onClick={() => setSelectedTopic(i)}>{topic.topic}</li>
                        ))}
                    </ul>
                }

                {selectedStandard !== null && selectedTopic !== null && selectedSubtopic === null &&
                    <ul className={list}>
                        {subtopics.map((subtopic, i) => (
                            <li key={i} className={`${item} ${unselectedItem} ${tooltip}`} onClick={() => setSelectedSubtopic(i)}>
                                <span>{subtopic.subtopic}</span>
                                {subtopic.description && <span className={tooltiptext}>{subtopic.description}</span>}
                            </li>
                        ))}
                    </ul>
                }

                {selectedStandard !== null && selectedTopic !== null && selectedSubtopic !== null &&
                    <div className="mb-3">
                        <span>Core Standard? </span>
                        <BS.Form.Select onChange={(e) => setCore(e.target.value === "yes")}>
                            <option value={"yes"}>Yes</option>
                            <option value={"no"} selected>No</option>
                        </BS.Form.Select>
                    </div>
                }
            </BS.Col>
        </BS.Row>
        {selectedStandard !== null && selectedTopic !== null && selectedSubtopic !== null && 
            <BS.Row style={{ cursor: "default" }}>
                <BS.Button onClick={addStandard}>Add Standard</BS.Button>
            </BS.Row>}
    </>
}
